import React from 'react';
import uniqueId from 'lodash.uniqueid';
import { useTheme, ThemeableElement } from '@design-systems/theme';
import { CheckmarkIcon } from '@ids-ts/icon';
import { B2 } from '@ids-ts/typography';
import clsx from 'clsx';
import logger from '@design-systems/logger';
import styles from './Menu.css';

export interface MenuItemProps extends ThemeableElement<'div'> {
  /**
   * Showing CheckMark on current selected item in Menu
   *
   * @default false
   */
  checkmark?: boolean;
  /**
   * Disable MenuItem if true.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * Set if the MenuItem is highlighted or not.
   *
   * @default false
   */
  highlighted?: boolean;
  /** Id for component. Assigns a unique id if not provided. */
  id?: string;
  /** Menu item label for Dropdown */
  menuItemLabel?: string | number;
  /** Callback function to be executed on click. */
  onClick?: React.MouseEventHandler;
  /** Callback function to be executed on keydown. */
  onKeyDown?: React.KeyboardEventHandler;
  /** Callback function to be executed on keyUp. */
  onKeyUp?: React.KeyboardEventHandler;
  /** Callback function to be executed on selection. Internal use only. */
  onSelect?: (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
  ) => void;
  /**
   * An ARIA role to be assigned to MenuItem.
   *
   * @default 'list'
   */
  role?: string;
  /**
   * Set if the MenuItem is selected or not.
   *
   * @default false
   */
  selected?: boolean;
  /**
   * Value to be assigned.
   *
   * @default ''
   */
  value?: string | number;
}

/**
 * MenuItem component used inside of Menu to display a line item
 *
 * @version PACKAGE_VERSION
 * @tutorial http://in/ids-storybook
 */
const MenuItem = ({
  checkmark = false,
  children,
  className,
  colorScheme,
  disabled = false,
  id,
  highlighted = false,
  menuItemLabel,
  onClick,
  onKeyDown,
  onKeyUp,
  onSelect,
  role = 'list',
  selected = false,
  theme,
  value = '',
  ...html
}: MenuItemProps) => {
  const { themeClass } = useTheme({ theme, colorScheme }, styles);
  const itemId = React.useRef(uniqueId('idsMenuItem'));
  const itemRef = React.useRef<HTMLDivElement>(null);
  const [keyDown, setKeyDown] = React.useState(false);
  const [mouseEntered, setMouseEntered] = React.useState(false);

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: validating that they are not using a prop and warning user.
    if (html.refReactDom !== undefined) {
      logger.error(
        'refReactDom is no longer supported, as we are now using a functional component.'
      );
    }
  });

  /** runs inside keyup and click events */
  const getUpdatedEvent = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (e.target) {
      const { target } = e;
      e.persist();
      e.target = {
        ...target,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: EventTarget does not have property value, why are we overriding it?
        value,
        attributes: itemRef.current && itemRef.current.attributes
      };
    }
  };

  /** handles click events */
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    getUpdatedEvent(event);
    onClick && onClick(event);
    onSelect && onSelect(event);
  };

  /** handles keyDown events */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setKeyDown(true);
    }

    onKeyDown && onKeyDown(event);
  };

  /** handles keyUp events */
  const handleKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (keyDown) {
        getUpdatedEvent(event);
        onSelect && onSelect(event);
        setKeyDown(false);
      }
    }

    onKeyUp && onKeyUp(event);
  };

  const isSelected = selected && !checkmark;

  /** set checkmark color on hover */
  const handleMouseEnter = () => {
    setMouseEntered(true);
  };

  /** reset checkmark color on hover leave */
  const handleMouseLeave = () => {
    setMouseEntered(false);
  };

  return (
    <B2
      as="ul"
      className={clsx(
        styles.menuItemWrapper,
        themeClass,
        className,
        disabled ? styles.menuItemDisabled : styles.menuItemNonDisabled,
        keyDown && styles.menuItemHasKeyDown,
        highlighted && !disabled && styles.menuItemIsHighlighted,
        isSelected && styles.menuItemIsSelected
      )}
      {...html}
      ref={itemRef}
      id={id || itemId.current}
      role={role}
      aria-selected={selected || highlighted}
      tabIndex={-1}
      data-menuitemlabel={menuItemLabel}
      onClick={disabled ? undefined : handleClick}
      onKeyDown={disabled ? undefined : handleKeyDown}
      onKeyUp={disabled ? undefined : handleKeyUp}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {checkmark && (
        <li className={clsx(styles.menuItemCheckmarkBox, themeClass)}>
          {selected && (
            <CheckmarkIcon
              width="12px"
              height="12px"
              className={clsx(styles.menuItemCheckmarkIconWrapper, themeClass, {
                [styles.menuItemCheckmarkIconWrapperMouseEntered]:
                  mouseEntered && !disabled,
                [styles.menuItemCheckmarkIconWrapperDisabled]: disabled
              })}
            />
          )}
        </li>
      )}
      <li
        className={clsx(
          styles.menuItemContainer,
          themeClass,
          disabled && styles.menuItemContainerDisabled,
          (keyDown || isSelected) && styles.menuItemContainerIsSelected
        )}
      >
        {children}
      </li>
    </B2>
  );
};

export default MenuItem;
